import React, { useState, useEffect } from "react";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Autorenew as AutorenewIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import {
    getCompanyRequest,
    addRole,
    deleteRole,
    reassessRequest,
    CompanyRequest,
    CompanyRequestRole,
    CompanyRequestStatus,
} from "../../api/companyRequestsApi";
import { SettingsContext } from "../../context/SettingsContext";
import { useContext } from "react";
import logger from "../../utils/logger";

const STATUS_COLORS: Record<CompanyRequestStatus, "default" | "primary" | "success" | "warning"> = {
    DRAFT: "default",
    ACTIVE: "primary",
    FILLED: "success",
    CLOSED: "warning",
};

export default function CompanyRequestDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { settings } = useContext(SettingsContext);
    const [request, setRequest] = useState<CompanyRequest | null>(null);
    const [loading, setLoading] = useState(false);
    const [addRoleOpen, setAddRoleOpen] = useState(false);
    const [newRoleProfession, setNewRoleProfession] = useState("");
    const [newRoleNeeded, setNewRoleNeeded] = useState<string>("1");
    const [newRoleDescription, setNewRoleDescription] = useState("");
    const [reassessing, setReassessing] = useState(false);
    const [reassessPolling, setReassessPolling] = useState(false);
    const [reassessToast, setReassessToast] = useState<{ open: boolean; message?: string; severity?: "success" | "warning" }>({ open: false });

    const requestId = id ? parseInt(id) : 0;

    const loadData = async (): Promise<CompanyRequest | null> => {
        if (!requestId) return null;
        setLoading(true);
        try {
            const req = await getCompanyRequest(requestId);
            setRequest(req);
            return req;
        } catch {
            logger.error("Failed to load request");
            return null;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [requestId]);

    const handleReassessRequest = async () => {
        if (!window.confirm("Re-run AI matching across every DRAFT/ACTIVE role on this request? Existing SUGGESTED matches will be replaced; CONFIRMED and REJECTED are preserved.")) {
            return;
        }
        setReassessing(true);
        try {
            await reassessRequest(requestId);
            setReassessPolling(true);
        } catch (err) {
            logger.error("Reassess request failed", err);
            window.alert("Failed to start reassessment — see console for details.");
        } finally {
            setReassessing(false);
        }
    };

    // Poll while any role on this request is still DRAFT (the processor is still working).
    useEffect(() => {
        if (!reassessPolling) return;
        let cancelled = false;
        const start = Date.now();
        const POLL_MS = 4000;
        const MAX_MS = 90_000;

        const tick = async () => {
            if (cancelled) return;
            const data = await loadData();
            if (cancelled) return;

            const draftRoleCount = data?.roles?.filter((r) => r.status === "DRAFT").length ?? 0;
            if (draftRoleCount === 0) {
                setReassessPolling(false);
                setReassessToast({
                    open: true,
                    severity: "success",
                    message: "Reassessment complete — open each role to review potential matches.",
                });
                return;
            }
            if (Date.now() - start > MAX_MS) {
                setReassessPolling(false);
                setReassessToast({
                    open: true,
                    severity: "warning",
                    message: `Reassessment finished but ${draftRoleCount} role${draftRoleCount === 1 ? "" : "s"} still DRAFT — no candidates passed filtering.`,
                });
                return;
            }
            setTimeout(tick, POLL_MS);
        };

        const startTimer = setTimeout(tick, POLL_MS);
        return () => {
            cancelled = true;
            clearTimeout(startTimer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reassessPolling]);

    const handleAddRole = async () => {
        if (!newRoleProfession) return;
        try {
            await addRole(requestId, {
                professionKey: newRoleProfession,
                numberOfWorkersNeeded: parseInt(newRoleNeeded, 10) || undefined,
                requirementsDescription: newRoleDescription || undefined,
            });
            setAddRoleOpen(false);
            setNewRoleProfession("");
            setNewRoleNeeded("1");
            setNewRoleDescription("");
            loadData();
        } catch {
            logger.error("Failed to add role");
        }
    };

    const handleDeleteRole = async (roleId: number) => {
        if (!window.confirm("Delete this role and all its matches?")) return;
        try {
            await deleteRole(requestId, roleId);
            loadData();
        } catch {
            logger.error("Failed to delete role");
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!request) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Request not found</Alert>
                <Button onClick={() => navigate("/company-requests")} sx={{ mt: 2 }}>Back to List</Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                <Tooltip title="Back">
                    <IconButton onClick={() => navigate("/company-requests")}>
                        <ArrowBackIcon />
                    </IconButton>
                </Tooltip>
                <h1>Request #{request.id}</h1>
                <Chip label={request.derivedStatus} color={STATUS_COLORS[request.derivedStatus]} />
                <Tooltip title="Refresh">
                    <IconButton onClick={() => loadData()}><RefreshIcon /></IconButton>
                </Tooltip>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title={request.derivedStatus === "FILLED" ? "Every role on this request is filled — reassessment disabled. Add or edit a role to re-enable." : ""}>
                    <span>
                        <Button
                            variant="outlined"
                            startIcon={reassessing || reassessPolling ? <CircularProgress size={18} /> : <AutorenewIcon />}
                            onClick={handleReassessRequest}
                            disabled={reassessing || reassessPolling || request.derivedStatus === "FILLED"}
                        >
                            {reassessPolling ? "Reassessing…" : "Reassess This Request"}
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {reassessPolling && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => setReassessPolling(false)}>
                            Stop polling
                        </Button>
                    }
                >
                    <AlertTitle>Reassessment in progress</AlertTitle>
                    Refreshing every 4s. Polling auto-stops when no role on this request is DRAFT.
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                    <Card>
                        <CardHeader title="Request Details" />
                        <CardContent>
                            <List dense>
                                <ListItem><ListItemText primary="Business" secondary={request.businessName || "-"} /></ListItem>
                                <ListItem><ListItemText primary="Account Manager" secondary={request.accountManagerId || "-"} /></ListItem>
                                <ListItem><ListItemText primary="Start Date" secondary={request.startDate ? new Date(request.startDate).toLocaleDateString() : "-"} /></ListItem>
                                <ListItem><ListItemText primary="End Date" secondary={request.endDate ? new Date(request.endDate).toLocaleDateString() : "-"} /></ListItem>
                                <ListItem><ListItemText primary="Location" secondary={request.location || "-"} /></ListItem>
                                <ListItem><ListItemText primary="Hours per Week" secondary={request.hoursPerWeek || "-"} /></ListItem>
                                <ListItem><ListItemText primary="Work Permit Required" secondary={request.requiresWorkPermit ? "Yes" : "No"} /></ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={7}>
                    <Card>
                        <CardHeader
                            title="Roles"
                            action={
                                <Button startIcon={<AddIcon />} onClick={() => setAddRoleOpen(true)} size="small" variant="contained">
                                    Add Role
                                </Button>
                            }
                        />
                        <CardContent>
                            {(request.roles ?? []).length === 0 ? (
                                <Alert severity="info">No roles yet — add one to start matching.</Alert>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                                            <TableRow>
                                                <TableCell>Role</TableCell>
                                                <TableCell>Profession</TableCell>
                                                <TableCell align="right">Needed</TableCell>
                                                <TableCell align="right">Confirmed</TableCell>
                                                <TableCell align="right">Suggestions</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(request.roles ?? []).map((role: CompanyRequestRole, idx: number) => (
                                                <TableRow key={role.id} hover>
                                                    <TableCell>{idx + 1}</TableCell>
                                                    <TableCell>{role.professionKey}</TableCell>
                                                    <TableCell align="right">{role.numberOfWorkersNeeded ?? "-"}</TableCell>
                                                    <TableCell align="right">{role.confirmedCount ?? 0}</TableCell>
                                                    <TableCell align="right">
                                                        {role.status === "FILLED" ? "N/A" : (role.matchCount ?? 0) - (role.confirmedCount ?? 0)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={role.status} color={STATUS_COLORS[role.status]} size="small" />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Button size="small" onClick={() => navigate(`/company-requests/${request.id}/roles/${role.id}`)}>
                                                            Open
                                                        </Button>
                                                        <Tooltip title="Delete role">
                                                            <IconButton size="small" color="error" onClick={() => handleDeleteRole(role.id)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Dialog open={addRoleOpen} onClose={() => setAddRoleOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Role</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
                        <TextField
                            select
                            label="Profession"
                            SelectProps={{ native: true }}
                            value={newRoleProfession}
                            onChange={(e) => setNewRoleProfession(e.target.value)}
                            required
                        >
                            <option value="" />
                            {(settings?.professions ?? []).map((p) => (
                                <option key={p.professionKey} value={p.professionKey}>
                                    {p.professionName}
                                </option>
                            ))}
                        </TextField>
                        <TextField
                            type="number"
                            label="Number of Workers Needed"
                            value={newRoleNeeded}
                            onChange={(e) => setNewRoleNeeded(e.target.value)}
                            inputProps={{ min: 1 }}
                        />
                        <TextField
                            label="Requirements Description"
                            multiline
                            rows={3}
                            value={newRoleDescription}
                            onChange={(e) => setNewRoleDescription(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddRoleOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddRole} variant="contained" disabled={!newRoleProfession}>Add</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={reassessToast.open}
                autoHideDuration={6000}
                onClose={() => setReassessToast({ open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setReassessToast({ open: false })}
                    severity={reassessToast.severity ?? "success"}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {reassessToast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
